import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { ListOwnership } from "@/enum/list.enum";
import { classNames } from "@/utils/classNames";

import { TabChip } from "@/components/TabChip/TabChip";

import { PinnedFilterTab } from "../utils/pinnedFilterTabs";
import { PinnedFilterTabButton } from "./PinnedFilterTabButton";

/** Per-ownership list counts for the tab number bubbles (PREC-3623). */
export type ListOwnershipCountMap = Partial<Record<ListOwnership, number>>;

/** Pseudo-tab value for the conditional "Search Results" tab (a separate axis from ownership). */
export const SEARCH_RESULTS_TAB = "SEARCH_RESULTS" as const;

export type ListTab = ListOwnership | typeof SEARCH_RESULTS_TAB;

interface ListOwnershipTabsProps {
	value: ListTab;
	onChange: (value: ListTab) => void;
	/** When true, append the otherwise-hidden "Search Results" tab. */
	showSearchTab: boolean;
	/** Per-ownership list counts for the tab bubbles (PREC-3623); a bubble is hidden when 0/absent. */
	counts?: ListOwnershipCountMap;
	/** User-pinned filter tabs rendered after the built-in tabs (PREC pin-to-tab). */
	pinnedTabs?: PinnedFilterTab[];
	/** Id of the pinned tab whose snapshot matches the current filters, if any. */
	activePinnedTabId?: number | null;
	onSelectPinnedTab?: (tab: PinnedFilterTab) => void;
	onRenamePinnedTab?: (id: number, name: string) => void;
	onUpdatePinnedTab?: (id: number) => void;
	onDeletePinnedTab?: (id: number) => void;
}

/**
 * Ownership tab strip for the List overview (My / Team / All lists).
 * Mirrors the tab-strip pattern used in the ReadyToSend header.
 *
 * When `showSearchTab` is set (a search term is active) a 4th "Search Results" tab is appended;
 * it represents searching across all ownership scopes rather than a single ownership value.
 *
 * User-pinned filter tabs (saved structured-filter snapshots) are rendered after the built-in
 * tabs, separated by a divider. When a pinned tab is active the built-in tabs are not highlighted.
 */
export const ListOwnershipTabs: FunctionComponent<ListOwnershipTabsProps> = ({
	value,
	onChange,
	showSearchTab,
	counts,
	pinnedTabs = [],
	activePinnedTabId = null,
	onSelectPinnedTab,
	onRenamePinnedTab,
	onUpdatePinnedTab,
	onDeletePinnedTab,
}) => {
	const { t } = useTranslation();

	const tabs: { label: string; value: ListTab }[] = [
		{ label: t("list.ownership.mine"), value: ListOwnership.MINE },
		{ label: t("list.ownership.team"), value: ListOwnership.TEAM },
		{ label: t("list.ownership.all"), value: ListOwnership.ALL },
		...(showSearchTab
			? [{ label: t("list.ownership.searchResults"), value: SEARCH_RESULTS_TAB }]
			: []),
	];

	return (
		<div className="flex bg-white border-b border-border-light w-full flex-shrink-0">
			{tabs.map((tab) => {
				const isActive = value === tab.value && !activePinnedTabId;
				// Counts are keyed by ownership only; the Search Results tab never shows a bubble.
				const count = tab.value === SEARCH_RESULTS_TAB ? undefined : counts?.[tab.value];

				return (
					<button
						key={tab.value}
						className={classNames(
							"inline-flex items-center px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer",
							isActive
								? "border-brand-light text-brand-light"
								: "border-transparent text-text-meta hover:text-text hover:border-gray-300"
						)}
						type="button"
						onClick={() => onChange(tab.value)}
					>
						{tab.label}
						{typeof count === "number" && count > 0 && (
							<TabChip active={isActive} label={`${count}`} />
						)}
					</button>
				);
			})}

			{pinnedTabs.length > 0 && (
				<div className="my-2 mx-1.5 w-px bg-border-light" aria-hidden="true" />
			)}

			{pinnedTabs.map((tab) => (
				<PinnedFilterTabButton
					key={tab.id}
					isActive={activePinnedTabId === tab.id}
					tab={tab}
					onDelete={() => onDeletePinnedTab?.(tab.id)}
					onRename={(name) => onRenamePinnedTab?.(tab.id, name)}
					onSelect={() => onSelectPinnedTab?.(tab)}
					onUpdate={() => onUpdatePinnedTab?.(tab.id)}
				/>
			))}
		</div>
	);
};
